<template>
  <div>
    <main-layout
      controlsBg="light"
      :hasControlsBar="false"
      :breadcrumb="getBreadcrumb">
      <div
        class="container">
        <grid-row
          column-align="center">
          <grid-col
            col-size="4">
            <h1
              class="header margin-top-xl">
              <div>
                <chevron
                  @click="$router.push({name: origin})"/>
                {{ $t('rawVariable.editRawVariable') }}
              </div>
              <GoToTagsAndCategorical
                origin="RawVariable"/>
            </h1>
            <h2>{{ this.clientReference }}</h2>
            <alert
              v-if="alertVisible"
              :message="alertMessage"
              :type="alertType">
            </alert>
          </grid-col>
        </grid-row>
        <form
          v-on:submit.prevent
          v-on:keypress.enter.prevent="onSave">
          <grid-row
            column-align="center">
            <grid-col
              col-size="4">
              <div
                class="variable-input-field variable-input-field-name">
                <label-input
                  :label="$t('aggregateVariable.name')"
                  v-model="name"
                  :errorMessage="$t('commons.mandatoryFieldMessage')"
                  class="margin-right-s">
                </label-input>
              </div>
              <div
                class="variable-input-field variable-input-field-unit">
                <label-input
                  :label="$t('aggregateVariable.unit')"
                  v-model="unit">
                </label-input>
              </div>
            </grid-col>
          </grid-row>
          <grid-row
            col-size="4"
            column-align="center">
            <grid-col
              col-size="4">
              <category-selector
                :label="$t('variableManagement.category')"
                :categories="getCategories"
                v-model="variableCategory">
              </category-selector>
            </grid-col>
          </grid-row>
          <grid-row
            col-size="4"
            column-align="center">
            <tags-editor
              class="variable-input-field variable-input-field-name"
              :tree="getTagsTree"
              v-model="tags"/>
          </grid-row>
          <grid-row
            column-align="center">
            <grid-col
              col-size="4">
              <div
                class="align-right margin-bottom-l">
                <btn-pill
                  :btnText="$t('commons.cancel')"
                  v-on:click="onCancel"
                  btn-size="m"
                  btn-type="secondary">
                </btn-pill>
                <btn-pill
                  :btnText="$t('commons.save')"
                  v-on:click="onSave"
                  btn-size="m">
                </btn-pill>
              </div>
            </grid-col>
          </grid-row>
        </form>
      </div>
    </main-layout>
  </div>
</template>

<script>
import MainLayout from '../MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import LabelInput from '@/components/shared/forms/LabelInput';
import BtnPill from '@/components/shared/buttons/BtnPill';
import Alert from '@/components/shared/alert/Alert';
import {mapActions, mapGetters} from 'vuex';
import TagsEditor from '@/components/variable/TagsEditor';
import Chevron from '@/components/shared/Chevron';
import GoToTagsAndCategorical from '@/components/variable/GoToTagsAndCategorical';
import CategorySelector from '@/components/variable/CategorySelector';

export default {
  components: {
    GoToTagsAndCategorical,
    TagsEditor,
    MainLayout,
    GridRow,
    GridCol,
    LabelInput,
    BtnPill,
    Alert,
    Chevron,
    CategorySelector
  },
  data () {
    return {
      origin: 'RawVariableList',
      editRawVariable: {},
      clientReference: '',
      /* Form fields */
      name: '',
      unit: '',
      variableCategory: null,
      tags: [],
      /* UI controls */
      alertVisible: false,
      alertMessage: '',
      alertType: '' // error, warning, success
    };
  },
  created () {
    this.retrieveTagsTree({ context: this });
    this.retrieveCategories({ context: this }).then(() => {
      this.variableCategory = getCategoryById(this.getCategories, this.editRawVariable.categoryId);
    });

    // Params
    const origin = this.$route.query.origin;
    if (origin) {
      this.origin = origin;
    }
    this.editRawVariable = this.$route.params;
    this.unit = this.editRawVariable.unit;
    this.name = this.editRawVariable.name;
    this.clientReference = this.editRawVariable.clientReference;
    this.tags = this.editRawVariable.tags;
  },
  computed: {
    ...mapGetters([
      'getTagsTree',
      'getCategories'
    ]),
    getBreadcrumb () {
      switch (this.origin) {
        case 'RawVariableList':
          return [
            { name: this.$t('menu.workspace'), link: '/workspace' },
            { name: this.$t('rawVariable.title'), link: '/raw-variable/list' },
            { name: this.$t('rawVariable.editRawVariable'), link: '/raw-variable', disabled: true }
          ];

        case 'VariableTagTree':
          return [
            { name: this.$t('menu.workspace'), link: '/workspace' },
            { name: this.$t('variableManagement.title'), link: '/variable-management' },
            { name: this.$t('variables.taxonomy'), link: '/tags-tree' },
            { name: this.$t('rawVariable.editRawVariable'), link: '/raw-variable', disabled: true }
          ];

        case 'VariableManagement':
          return [
            { name: this.$t('menu.workspace'), link: '/workspace' },
            { name: this.$t('variableManagement.title'), link: '/variable-management' },
            { name: this.$t('rawVariable.editRawVariable'), link: '/raw-variable', disabled: true }
          ];
      }
    }
  },
  methods: {
    ...mapActions([
      'updateRawVariable',
      'retrieveTagsTree',
      'retrieveCategories'
    ]),
    onSave () {
      clearErrorPrompts(this);
      const rawVariable = this.editRawVariable;
      rawVariable.name = this.name;
      rawVariable.unit = this.unit;
      rawVariable.tags = this.tags;
      if (this.variableCategory !== null) {
        rawVariable.categoryId = this.variableCategory.id;
      } else {
        rawVariable.categoryId = null;
      }
      updateRawVariable(this, rawVariable);
    },
    onCancel () {
      this.$router.push({name: this.origin});
    }
  }
};

const clearErrorPrompts = context => {
  context.nameError = false;
};

const updateRawVariable = (context, rawVariable) => {
  const msg = context.$t('aggregateVariable.successUpdate', {name: rawVariable.clientReference});
  context.updateRawVariable({context: context, rawVariable}).then(() => {
    context.$router.replace({ name: context.origin, params: {alert: msg} });
  }).catch(() => {
    showAlert(context, context.$t('aggregateVariable.errorUpdate', {name: rawVariable.clientReference}), 'error');
  });
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  context.showAlert = true;
  setTimeout(() => {
    context.alertVisible = false;
  }, 2000);
};

const getCategoryById = (list, ref) => {
  const id = Number(ref);
  for (let i = 0; i < list.length; i++) {
    if (id === list[i].value) {
      return list[i];
    }
  }
  return null;
};
</script>

<style lang="scss" scoped>
  @import "src/stylesheets/variables";

  .align-right {
    float: right;
    clear:both;
  }

  .variable-input-field {
    display: inline-block;
  }

  .variable-input-field-name {
    width: 75%;
    margin: 0 0;
  }

  .variable-input-field-unit {
    min-width: 0px;
    width: 25%;
    float: right;
    margin: 0 0;
  }

  .submit-buttons {
    float: right;
  }

  h2 {
    margin-top: $sp-margin-large;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

</style>



// WEBPACK FOOTER //
// src/layouts/variables/RawVariable.vue